import { useState, useEffect } from 'react'
import { PlusIcon, MagnifyingGlassIcon, PlayIcon, CheckIcon, XMarkIcon, EyeIcon, PrinterIcon, TrashIcon, ShoppingCartIcon } from '@heroicons/react/24/outline'
import { norm } from '../../utils/normalize'
import { fmtDate } from '../../utils/format'
import { useToast } from '../../contexts/ToastContext'
import SortableHeader, { useSort } from '../../components/SortableHeader'

const API_BASE_URL = '/api/v1/dev'

interface Allergen {
  id: number
  name: string
  code: string
  svg_icon: string
}

interface Recipe {
  id: number
  name: string
  alias: string
  code: string
  type: string
  finished_product_id?: number
  final_unit: string
  final_quantity_expected: string
  shelf_life_days?: number
}

interface ProductionOrder {
  id: number
  code: string
  recipe: {
    id: number
    name: string
    alias?: string
    final_unit: string
  }
  target_quantity: string
  actual_quantity: string | null
  yield_percentage: string | null
  status: 'draft' | 'in_progress' | 'completed' | 'cancelled'
  started_at: string | null
  completed_at: string | null
  creator: string | null
  notes: string | null
}

interface Requirement {
  ingredient_id: number
  ingredient_name: string
  required_quantity_g: number
  availability: {
    is_available: boolean
    total_available_g: number
    required_g: number
    shortage_g: number
    batch_allocation: Array<{
      id: number
      batch_code: string
      lot_number: string
      quantity_to_use_g: number
      quantity_available_g: number
      expiry_date: string | null
      status: string
      supplier_name: string
      brand: string
      cost_per_kg: number
    }>
  }
}

interface BatchAllocation {
  batch_id: number
  quantity_g: number
}

interface IngredientAllocation {
  ingredient_id: number
  ingredient_name: string
  required_g: number
  batches: BatchAllocation[]
}

export default function ProductionOrders() {
  const toast = useToast()
  const [printerModes, setPrinterModes] = useState<Record<string, string>>({})
  const [suppliers, setSuppliers] = useState<Array<{ id: number; trade_name: string }>>([])
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  const [purchaseItems, setPurchaseItems] = useState<Array<{ ingredient_id: number; name: string; quantity: number; unit_id: number; unit_name: string; supplier_id: string }>>([])
  const [savingPurchase, setSavingPurchase] = useState(false)
  const [showProductionModal, setShowProductionModal] = useState(false)
  const [productionItems, setProductionItems] = useState<Array<{ ingredient_id: number; name: string; recipe_id: number; target_quantity: number; unit_name: string }>>([])
  const [savingProduction, setSavingProduction] = useState(false)
  const [printing, setPrinting] = useState<string | null>(null)
  const [orders, setOrders] = useState<ProductionOrder[]>([])
  const [recipes, setRecipes] = useState<Recipe[]>([])
  const [allergens, setAllergens] = useState<Allergen[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showCompleteModal, setShowCompleteModal] = useState(false)
  const [showStartModal, setShowStartModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<any>(null)
  const [orderToComplete, setOrderToComplete] = useState<ProductionOrder | null>(null)
  const [orderToStart, setOrderToStart] = useState<ProductionOrder | null>(null)
  const [startRequirements, setStartRequirements] = useState<Requirement[]>([])
  const [batchAllocations, setBatchAllocations] = useState<IngredientAllocation[]>([])
  const [inheritedAllergens, setInheritedAllergens] = useState<number[]>([])
  const [error, setError] = useState('')

  const [formData, setFormData] = useState({
    recipe_id: 0,
    target_quantity: '',
    notes: ''
  })

  const [completeFormData, setCompleteFormData] = useState({
    actual_quantity: '',
    extra_allergens: [] as number[],
    notes: ''
  })

  const [requirements, setRequirements] = useState<Requirement[]>([])

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    await Promise.all([fetchOrders(), fetchRecipes(), fetchAllergens(), fetchPrinterModes(), fetchSuppliers()])
    setLoading(false)
  }

  const fetchSuppliers = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/suppliers`, { headers: { Accept: 'application/json' } })
      const d = await r.json()
      if (r.ok && d.data) setSuppliers(d.data.filter((s: any) => !s.is_self))
    } catch { /* noop */ }
  }

  const fetchPrinterModes = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/printers`, { headers: { Accept: 'application/json' } })
      const d = await r.json()
      if (r.ok && d.success) {
        const m: Record<string, string> = {}
        d.data.forEach((p: any) => { m[p.key] = p.mode })
        setPrinterModes(m)
      }
    } catch { /* noop */ }
  }

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/production-orders`, {
        headers: { 'Accept': 'application/json' }
      })
      const data = await response.json()
      if (response.ok && data.success) {
        setOrders(data.data)
      }
    } catch (error) {
      console.error('Error fetching orders:', error)
    }
  }

  const fetchRecipes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/recipes`)
      const data = await response.json()
      if (response.ok && data.success) {
        setRecipes(data.data)
      }
    } catch (error) {
      console.error('Error fetching recipes:', error)
    }
  }

  const fetchAllergens = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/allergens`)
      const data = await response.json()
      if (response.ok && data.data) {
        setAllergens(data.data)
      }
    } catch (error) {
      console.error('Error fetching allergens:', error)
    }
  }

  const checkAvailability = async () => {
    if (!formData.recipe_id || !formData.target_quantity) {
      return
    }

    try {
      const recipe = recipes.find(r => r.id === formData.recipe_id)
      if (!recipe) return

      const response = await fetch(`${API_BASE_URL}/recipes/${recipe.id}/check-availability`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          target_quantity: parseFloat(formData.target_quantity)
        })
      })

      const data = await response.json()
      if (response.ok && data.success) {
        setRequirements(data.data.requirements)
      }
    } catch (error) {
      console.error('Error checking availability:', error)
    }
  }

  useEffect(() => {
    if (formData.recipe_id && formData.target_quantity) {
      const timeoutId = setTimeout(() => {
        checkAvailability()
      }, 500)
      return () => clearTimeout(timeoutId)
    } else {
      setRequirements([])
    }
  }, [formData.recipe_id, formData.target_quantity])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    try {
      const response = await fetch(`${API_BASE_URL}/production-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipe_id: formData.recipe_id,
          target_quantity: parseFloat(formData.target_quantity),
          notes: formData.notes || null
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        await fetchOrders()
        setShowCreateModal(false)
        setFormData({ recipe_id: 0, target_quantity: '', notes: '' })
        setRequirements([])
      } else {
        setError(data.message || 'Error creando orden de producción')
      }
    } catch (error) {
      setError('Error de conexión')
    }
  }

  const openStartModal = async (order: ProductionOrder) => {
    console.log('=== openStartModal called ===', order)
    setOrderToStart(order)
    setError('')

    try {
      // Get the requirements with batch allocations
      const response = await fetch(`${API_BASE_URL}/recipes/${order.recipe.id}/check-availability`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          target_quantity: parseFloat(order.target_quantity)
        })
      })

      const data = await response.json()
      console.log('Check availability response:', data)

      if (response.ok && data.success) {
        const reqs = data.data.requirements as Requirement[]
        console.log('Requirements:', reqs)
        setStartRequirements(reqs)

        // Initialize batch allocations with suggested FIFO allocation
        const initialAllocations: IngredientAllocation[] = reqs.map(req => {
          const batches: BatchAllocation[] = []
          let remaining = req.required_quantity_g

          console.log(`Processing ingredient ${req.ingredient_name}:`, {
            required: req.required_quantity_g,
            available_batches: req.availability.batch_allocation
          })

          // Auto-allocate from available batches (FIFO by expiry)
          for (const batch of req.availability.batch_allocation) {
            if (remaining <= 0) break
            console.log('  Batch:', batch)
            const toUse = Math.min(remaining, parseFloat(String(batch.quantity_available_g)))
            if (toUse > 0) {
              batches.push({
                batch_id: batch.id,
                quantity_g: toUse
              })
              remaining -= toUse
            }
          }

          console.log(`  Allocated batches:`, batches)

          return {
            ingredient_id: req.ingredient_id,
            ingredient_name: req.ingredient_name,
            required_g: req.required_quantity_g,
            batches
          }
        })

        setBatchAllocations(initialAllocations)
        setShowStartModal(true)
      } else {
        setError(data.message || 'Error cargando requisitos')
      }
    } catch (error) {
      setError('Error de conexión')
    }
  }

  const updateBatchAllocation = (ingredientId: number, batchId: number, quantity: number) => {
    setBatchAllocations(prev => prev.map(alloc => {
      if (alloc.ingredient_id !== ingredientId) return alloc

      const existingBatchIndex = alloc.batches.findIndex(b => b.batch_id === batchId)

      if (quantity <= 0) {
        // Remove batch if quantity is 0
        return {
          ...alloc,
          batches: alloc.batches.filter(b => b.batch_id !== batchId)
        }
      } else if (existingBatchIndex >= 0) {
        // Update existing batch
        const newBatches = [...alloc.batches]
        newBatches[existingBatchIndex] = { batch_id: batchId, quantity_g: quantity }
        return { ...alloc, batches: newBatches }
      } else {
        // Add new batch
        return {
          ...alloc,
          batches: [...alloc.batches, { batch_id: batchId, quantity_g: quantity }]
        }
      }
    }))
  }

  const getTotalAllocated = (ingredientId: number): number => {
    const alloc = batchAllocations.find(a => a.ingredient_id === ingredientId)
    if (!alloc) return 0
    return alloc.batches.reduce((sum, b) => sum + b.quantity_g, 0)
  }

  const handleStartSubmit = async () => {
    if (!orderToStart) return

    // Validate all ingredients have enough allocated
    for (const alloc of batchAllocations) {
      const total = alloc.batches.reduce((sum, b) => sum + b.quantity_g, 0)
      if (total < alloc.required_g * 0.99) { // Allow 1% tolerance
        setError(`Falta asignar cantidad para: ${alloc.ingredient_name}`)
        return
      }
    }

    try {
      // Filter out allocations with no batches (after filtering 0 quantities)
      const filteredAllocations = batchAllocations
        .map(alloc => ({
          ingredient_id: alloc.ingredient_id,
          batches: alloc.batches.filter(b => b.quantity_g > 0)
        }))
        .filter(alloc => alloc.batches.length > 0)

      const payload = {
        batch_allocations: filteredAllocations
      }
      console.log('Sending batch allocations:', JSON.stringify(payload, null, 2))

      const response = await fetch(`${API_BASE_URL}/production-orders/${orderToStart.id}/start`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      const data = await response.json()
      console.log('Response:', data)

      if (response.ok && data.success) {
        await fetchOrders()
        setShowStartModal(false)
        setOrderToStart(null)
        setBatchAllocations([])
        setError('')
      } else {
        // Handle validation errors
        if (data.errors) {
          const errorMessages = Object.values(data.errors).flat().join(', ')
          setError(errorMessages || data.message || 'Error de validación')
        } else {
          setError(data.message || 'Error iniciando orden')
        }
      }
    } catch (error) {
      console.error('Error:', error)
      setError('Error de conexión')
    }
  }

  const openCompleteModal = async (order: ProductionOrder) => {
    setOrderToComplete(order)
    setCompleteFormData({
      actual_quantity: order.target_quantity,
      extra_allergens: [],
      notes: ''
    })

    // Load inherited allergens from recipe
    try {
      const response = await fetch(`${API_BASE_URL}/recipes/${order.recipe.id}`)
      const data = await response.json()
      if (response.ok && data.success) {
        // Get allergen IDs from recipe items
        const allergenIds: number[] = []
        data.data.items.forEach((item: any) => {
          if (item.ingredient.allergens) {
            item.ingredient.allergens.forEach((allergen: any) => {
              // allergen might be just a name string or an object with id
              if (typeof allergen === 'object' && allergen.id) {
                allergenIds.push(allergen.id)
              }
            })
          }
        })
        // Also include allergens from the recipe itself if available
        if (data.data.recipe.allergens) {
          data.data.recipe.allergens.forEach((allergenId: number) => {
            if (!allergenIds.includes(allergenId)) {
              allergenIds.push(allergenId)
            }
          })
        }
        setInheritedAllergens([...new Set(allergenIds)])
      }
    } catch (error) {
      console.error('Error loading recipe allergens:', error)
      setInheritedAllergens([])
    }

    setShowCompleteModal(true)
  }

  const handleCompleteSubmit = async () => {
    if (!orderToComplete) return
    if (!completeFormData.actual_quantity || parseFloat(completeFormData.actual_quantity) <= 0) {
      setError('La cantidad producida debe ser mayor a 0')
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/production-orders/${orderToComplete.id}/complete`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          actual_quantity: parseFloat(completeFormData.actual_quantity),
          extra_allergens: completeFormData.extra_allergens,
          notes: completeFormData.notes || null
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        await fetchOrders()
        setShowCompleteModal(false)
        setOrderToComplete(null)
        setError('')
        // Show success message
        alert(data.message || 'Orden completada exitosamente')
      } else {
        setError(data.message || 'Error completando orden')
      }
    } catch (error) {
      setError('Error de conexión')
    }
  }

  const toggleExtraAllergen = (allergenId: number) => {
    const current = completeFormData.extra_allergens
    if (current.includes(allergenId)) {
      setCompleteFormData({
        ...completeFormData,
        extra_allergens: current.filter(id => id !== allergenId)
      })
    } else {
      setCompleteFormData({
        ...completeFormData,
        extra_allergens: [...current, allergenId]
      })
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que deseas eliminar esta orden de producción?')) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/production-orders/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (response.ok && data.success) {
        await fetchOrders()
      } else {
        setError(data.message || 'Error eliminando orden')
      }
    } catch (error) {
      setError('Error de conexión')
    }
  }

  const handleView = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/production-orders/${id}`, {
        headers: { 'Accept': 'application/json' }
      })
      const data = await response.json()
      if (response.ok && data.success) {
        setSelectedOrder(data.data)
        setShowViewModal(true)
      }
    } catch (error) {
      setError('Error cargando orden')
    }
  }

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    }
    const labels = {
      draft: 'Borrador',
      in_progress: 'En Progreso',
      completed: 'Completada',
      cancelled: 'Cancelada'
    }
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badges[status as keyof typeof badges]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    )
  }

  // Mapeo documento -> impresora configurada
  const TYPE_TO_PRINTER: Record<string, string> = { order: 'tickets', label: 'etiquetas', report: 'folios' }

  // Imprime según el modo de la impresora: local abre el PDF; ipp/cups lo envía
  const openPdf = async (type: 'order' | 'label' | 'report') => {
    const id = selectedOrder?.order?.id
    if (!id || printing) return
    const mode = printerModes[TYPE_TO_PRINTER[type]] || 'local'
    if (mode === 'local') {
      window.open(`${API_BASE_URL}/production-orders/${id}/print/${type}`, '_blank')
      return
    }
    setPrinting(type)
    try {
      const res = await fetch(`${API_BASE_URL}/production-orders/${id}/print/${type}/send`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      })
      const data = await res.json()
      if (res.ok && data.success) toast.success(data.message || 'Enviado a la impresora')
      else toast.error(data.message || 'No se pudo imprimir')
    } catch {
      toast.error('Error de conexión con la impresora')
    } finally {
      setPrinting(null)
    }
  }

  // Ingredientes faltantes (shortage > 0) de la orden actualmente visualizada
  const shortageItems = (o: any): Array<{ ingredient_id: number; ingredient_name: string; quantity_g: number }> =>
    (o?.requirements?.requirements || [])
      .filter((r: Requirement) => r.availability && r.availability.shortage_g > 0)
      .map((r: Requirement) => ({
        ingredient_id: r.ingredient_id,
        ingredient_name: r.ingredient_name,
        quantity_g: Math.ceil(r.availability.shortage_g),
      }))

  // Abre el modal de generación de órdenes de compra con los faltantes NO
  // elaborados, convertidos a su unidad por defecto, para asignar proveedor por ítem.
  const generatePurchaseOrder = async () => {
    const items = shortageItems(selectedOrder)
    if (items.length === 0) return

    try {
      const response = await fetch(`${API_BASE_URL}/ingredients/convert-from-grams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items.map(i => ({ ingredient_id: i.ingredient_id, grams: i.quantity_g })) }),
      })
      const data = await response.json()
      if (!response.ok || !data.success) {
        toast.error('No se pudieron preparar los faltantes')
        return
      }
      // Sólo ingredientes que se compran (se excluyen los elaborados)
      const comprables = data.data
        .filter((d: any) => d.type !== 'elaborated')
        .map((d: any) => ({
          ingredient_id: d.ingredient_id,
          name: d.name,
          // No tiene sentido comprar menos de 1 unidad de medida → mínimo 1
          quantity: Math.max(1, d.quantity),
          unit_id: d.unit_id,
          unit_name: d.unit_name,
          supplier_id: d.preferred_supplier_id ? String(d.preferred_supplier_id) : '',
        }))
      if (comprables.length === 0) {
        toast.info('No hay ingredientes que comprar (todos los faltantes son elaborados)')
        return
      }
      setPurchaseItems(comprables)
      setShowPurchaseModal(true)
    } catch {
      toast.error('Error de conexión')
    }
  }

  // Crea una orden de compra por cada proveedor (agrupando los ítems asignados)
  const createPurchaseOrders = async () => {
    if (purchaseItems.some(i => !i.supplier_id)) {
      toast.error('Asigna un proveedor a cada ingrediente')
      return
    }
    setSavingPurchase(true)
    try {
      const groups: Record<string, any[]> = {}
      purchaseItems.forEach(i => {
        (groups[i.supplier_id] ||= []).push(i)
      })
      let creadas = 0
      for (const [supplierId, items] of Object.entries(groups)) {
        const res = await fetch(`${API_BASE_URL}/purchase-orders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            supplier_id: Number(supplierId),
            notes: `Generada desde la orden de producción ${selectedOrder?.order?.code ?? ''}`,
            items: items.map(i => ({ ingredient_id: i.ingredient_id, quantity: i.quantity, unit_id: i.unit_id })),
          }),
        })
        if (res.ok) creadas++
      }
      if (creadas > 0) {
        toast.success(`${creadas} orden(es) de compra creada(s)`)
        setShowPurchaseModal(false)
        setPurchaseItems([])
      } else {
        toast.error('No se pudo crear ninguna orden de compra')
      }
    } catch {
      toast.error('Error de conexión')
    } finally {
      setSavingPurchase(false)
    }
  }

  // Receta de ingrediente elaborado (elaborated_ingredient) para un ingrediente
  const recipeForElaborated = (ingredientId: number) =>
    recipes.find(r => r.finished_product_id === ingredientId && r.type === 'elaborated_ingredient')

  // Faltantes que son ingredientes elaborados con receta (se pueden producir)
  const elaboratedShortages = (o: any) =>
    shortageItems(o).filter(s => recipeForElaborated(s.ingredient_id))

  // Abre el modal para generar órdenes de producción de los faltantes elaborados
  const generateProductionOrders = async () => {
    const items = shortageItems(selectedOrder)
    if (items.length === 0) return
    try {
      const response = await fetch(`${API_BASE_URL}/ingredients/convert-from-grams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items.map(i => ({ ingredient_id: i.ingredient_id, grams: i.quantity_g })) }),
      })
      const data = await response.json()
      if (!response.ok || !data.success) {
        toast.error('No se pudieron preparar los faltantes')
        return
      }
      const elaborados = data.data
        .filter((d: any) => d.type === 'elaborated')
        .map((d: any) => {
          const recipe = recipeForElaborated(d.ingredient_id)
          return recipe ? {
            ingredient_id: d.ingredient_id,
            name: d.name,
            recipe_id: recipe.id,
            target_quantity: Math.max(1, d.quantity),
            unit_name: recipe.final_unit || d.unit_name,
          } : null
        })
        .filter(Boolean)
      if (elaborados.length === 0) {
        toast.info('No hay ingredientes elaborados faltantes con receta')
        return
      }
      setProductionItems(elaborados)
      setShowProductionModal(true)
    } catch {
      toast.error('Error de conexión')
    }
  }

  // Crea una orden de producción por cada ingrediente elaborado faltante
  const createProductionOrders = async () => {
    if (productionItems.some(i => !i.target_quantity || i.target_quantity <= 0)) {
      toast.error('Indica la cantidad objetivo de cada elaborado')
      return
    }
    setSavingProduction(true)
    try {
      let creadas = 0
      for (const it of productionItems) {
        const res = await fetch(`${API_BASE_URL}/production-orders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            recipe_id: it.recipe_id,
            target_quantity: it.target_quantity,
            notes: `Generada desde la orden de producción ${selectedOrder?.order?.code ?? ''}`,
          }),
        })
        if (res.ok) creadas++
      }
      if (creadas > 0) {
        toast.success(`${creadas} orden(es) de producción creada(s)`)
        setShowProductionModal(false)
        setProductionItems([])
        fetchOrders()
      } else {
        toast.error('No se pudo crear ninguna orden de producción')
      }
    } catch {
      toast.error('Error de conexión')
    } finally {
      setSavingProduction(false)
    }
  }

  // Lotes asignados a un ingrediente (desde los consumos registrados al iniciar)
  const lotsFor = (ingredientId: number): string => {
    const lots = (selectedOrder?.consumptions || [])
      .filter((c: any) => c.ingredient_id === ingredientId)
      .map((c: any) => c.internal_code || c.lot_number)
      .filter(Boolean)
    return lots.length > 0 ? lots.join(', ') : '-'
  }


  const [currentPage, setCurrentPage] = useState(1)
  const { sortField, sortDirection, handleSort, sortData } = useSort<ProductionOrder>('code')
  const perPage = 15

  const filteredOrders = orders.filter(order => {
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter
    const matchesSearch = norm(order.code).includes(norm(searchTerm)) ||
                         norm(order.recipe.name).includes(norm(searchTerm))
    return matchesStatus && matchesSearch
  })

  const sortedOrders = sortData(filteredOrders)
  const totalPages = Math.ceil(sortedOrders.length / perPage)
  const paginatedOrders = sortedOrders.slice((currentPage - 1) * perPage, currentPage * perPage)

  useEffect(() => { setCurrentPage(1) }, [searchTerm, statusFilter])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Órdenes de Producción</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gestión de órdenes de producción basadas en recetas
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Nueva Orden
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
          <button onClick={() => setError('')} className="float-right font-bold">×</button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por código o receta..."
                className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estado
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">Todos los estados</option>
              <option value="draft">Borrador</option>
              <option value="in_progress">En Progreso</option>
              <option value="completed">Completada</option>
              <option value="cancelled">Cancelada</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Código" field="code" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Receta" field="recipe.name" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Alias" field="recipe.alias" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cantidad Objetivo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cantidad Real
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rendimiento
                </th>
                <SortableHeader label="Estado" field="status" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {order.code}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{order.recipe.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{order.recipe.alias || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {parseFloat(order.target_quantity).toFixed(2)} {order.recipe.final_unit}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {order.actual_quantity ? `${parseFloat(order.actual_quantity).toFixed(2)} ${order.recipe.final_unit}` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {order.yield_percentage ? `${parseFloat(order.yield_percentage).toFixed(1)}%` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(order.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleView(order.id)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Ver detalles"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      {order.status === 'draft' && (
                        <button
                          onClick={() => openStartModal(order)}
                          className="text-green-600 hover:text-green-900"
                          title="Iniciar producción"
                        >
                          <PlayIcon className="h-4 w-4" />
                        </button>
                      )}
                      {order.status === 'in_progress' && (
                        <button
                          onClick={() => openCompleteModal(order)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Completar"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                      )}
                      {(order.status === 'draft' || order.status === 'cancelled') && (
                        <button
                          onClick={() => handleDelete(order.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Página {currentPage} de {totalPages} ({sortedOrders.length} órdenes)
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Nueva Orden de Producción
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setFormData({ recipe_id: 0, target_quantity: '', notes: '' })
                  setRequirements([])
                  setError('')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Receta *
                  </label>
                  <select
                    value={formData.recipe_id}
                    onChange={(e) => setFormData({ ...formData, recipe_id: parseInt(e.target.value) })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value={0}>Seleccionar receta</option>
                    {recipes.map(recipe => (
                      <option key={recipe.id} value={recipe.id}>
                        {recipe.alias || recipe.code} ({recipe.name})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cantidad Objetivo ({recipes.find(r => r.id === formData.recipe_id)?.final_unit || 'selecciona receta'}) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.target_quantity}
                    onChange={(e) => setFormData({ ...formData, target_quantity: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    La receta produce {recipes.find(r => r.id === formData.recipe_id)?.final_quantity_expected || '?'} {recipes.find(r => r.id === formData.recipe_id)?.final_unit || ''} por lote
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Notas adicionales..."
                />
              </div>

              {/* Ingredients Table */}
              {requirements.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    Ingredientes Necesarios
                  </h4>
                  <div className="border rounded-md overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ingrediente
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Necesario
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Disponible
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Faltante
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Estado
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {requirements.map((req, index) => (
                          <tr key={index} className={req.availability.is_available ? '' : 'bg-red-50'}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {req.ingredient_name}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-900">
                              {req.required_quantity_g.toFixed(2)} g
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-900">
                              {req.availability.total_available_g.toFixed(2)} g
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-900">
                              {req.availability.shortage_g > 0 ? (
                                <span className="text-red-600 font-semibold">
                                  {req.availability.shortage_g.toFixed(2)} g
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-center">
                              {req.availability.is_available ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  ✓ OK
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  ✗ Insuficiente
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan={5} className="px-4 py-2 text-xs text-gray-500 italic">
                            {requirements.every(r => r.availability.is_available) ? (
                              <span className="text-green-600 font-medium">✓ Todos los ingredientes están disponibles</span>
                            ) : (
                              <span className="text-amber-600 font-medium">⚠ Hay ingredientes insuficientes. Puedes crear la orden, pero no podrás iniciar la producción hasta tener stock.</span>
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    setFormData({ recipe_id: 0, target_quantity: '', notes: '' })
                    setRequirements([])
                    setError('')
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Crear Orden
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedOrder && (
        <>
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-5xl shadow-lg rounded-md bg-white mb-10">
              <div className="flex justify-between items-center mb-4 no-print">
                <h3 className="text-lg font-medium text-gray-900">
                  Detalles de Orden de Producción
                </h3>
                <div className="flex gap-2">
                  {selectedOrder.order.status === 'completed' ? (
                    <>
                      <button
                        onClick={() => openPdf('label')}
                        disabled={!!printing}
                        className="text-gray-600 hover:text-gray-900 flex items-center gap-1 px-3 py-1 border border-gray-300 rounded-md"
                        title="Imprimir etiqueta (ingredientes y alérgenos)"
                      >
                        <PrinterIcon className="h-5 w-5" />
                        <span className="text-sm">Etiqueta</span>
                      </button>
                      <button
                        onClick={() => openPdf('report')}
                        disabled={!!printing}
                        className="text-gray-600 hover:text-gray-900 flex items-center gap-1 px-3 py-1 border border-gray-300 rounded-md"
                        title="Imprimir estado (lotes y caducidades)"
                      >
                        <PrinterIcon className="h-5 w-5" />
                        <span className="text-sm">Estado</span>
                      </button>
                    </>
                  ) : (selectedOrder.order.status !== 'cancelled' && shortageItems(selectedOrder).length > 0) ? (
                    <>
                      <button
                        onClick={generatePurchaseOrder}
                        className="text-amber-700 hover:text-amber-900 flex items-center gap-1 px-3 py-1 border border-amber-300 bg-amber-50 rounded-md"
                        title="Generar orden de compra con los ingredientes faltantes"
                      >
                        <ShoppingCartIcon className="h-5 w-5" />
                        <span className="text-sm">OC de faltantes</span>
                      </button>
                      {elaboratedShortages(selectedOrder).length > 0 && (
                        <button
                          onClick={generateProductionOrders}
                          className="text-indigo-700 hover:text-indigo-900 flex items-center gap-1 px-3 py-1 border border-indigo-300 bg-indigo-50 rounded-md"
                          title="Generar órdenes de producción para los ingredientes elaborados faltantes"
                        >
                          <PlayIcon className="h-5 w-5" />
                          <span className="text-sm">Producir elaborados</span>
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => openPdf('order')}
                      disabled={!!printing}
                      className="text-gray-600 hover:text-gray-900 flex items-center gap-1 px-3 py-1 border border-gray-300 rounded-md"
                      title="Imprimir orden de producción"
                    >
                      <PrinterIcon className="h-5 w-5" />
                      <span className="text-sm">Imprimir</span>
                    </button>
                  )}
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div id="printable-section" className="space-y-6">
                {/* Print Header */}
                <div className="hidden print:block text-center mb-6">
                  <h1 className="text-2xl font-bold text-gray-900">APPCC Pastelería Pavlova</h1>
                  <h2 className="text-xl font-semibold text-gray-700 mt-2">Orden de Producción</h2>
                </div>

                {/* Order Info */}
                <div className="bg-gray-50 p-4 rounded-md print:bg-white print:border print:border-gray-300">
                  <h4 className="text-sm font-bold text-gray-700 uppercase mb-3 print:text-base">Información General</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:grid-cols-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase">Código Orden</label>
                      <p className="mt-1 text-sm font-bold text-gray-900 print:text-base">{selectedOrder.order.code}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase">Receta</label>
                      <p className="mt-1 text-sm font-semibold text-gray-900 print:text-base">{selectedOrder.recipe.name}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase">Alias</label>
                      <p className="mt-1 text-sm text-gray-900 print:text-base">{selectedOrder.recipe.alias || '-'}</p>
                    </div>
                    <div className="no-print">
                      <label className="block text-xs font-medium text-gray-500 uppercase">Estado</label>
                      <p className="mt-1">{getStatusBadge(selectedOrder.order.status)}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase">Cantidad Objetivo</label>
                      <p className="mt-1 text-sm font-semibold text-gray-900 print:text-base">
                        {parseFloat(selectedOrder.order.target_quantity).toFixed(2)} {selectedOrder.recipe.final_unit}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase">Cantidad Real</label>
                      <p className="mt-1 text-sm text-gray-900 print:text-base">
                        {selectedOrder.order.actual_quantity ?
                          `${parseFloat(selectedOrder.order.actual_quantity).toFixed(2)} ${selectedOrder.recipe.final_unit}` :
                          '_____________'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase">Rendimiento</label>
                      <p className="mt-1 text-sm text-gray-900 print:text-base">
                        {selectedOrder.order.yield_percentage ?
                          `${parseFloat(selectedOrder.order.yield_percentage).toFixed(2)}%` :
                          '_____________'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase">Fecha Creación</label>
                      <p className="mt-1 text-sm text-gray-900 print:text-base">
                        {fmtDate(selectedOrder.order.created_at)}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase">Fecha Inicio</label>
                      <p className="mt-1 text-sm text-gray-900 print:text-base">
                        {selectedOrder.order.started_at ? fmtDate(selectedOrder.order.started_at) : '_____________'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase">Fecha Completado</label>
                      <p className="mt-1 text-sm text-gray-900 print:text-base">
                        {selectedOrder.order.completed_at ? fmtDate(selectedOrder.order.completed_at) : '_____________'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Ingredients List */}
                {selectedOrder.requirements && selectedOrder.requirements.requirements.length > 0 && (
                  <div className="print:break-inside-avoid">
                    <h4 className="text-md font-bold text-gray-900 mb-3 print:text-lg print:border-b-2 print:border-gray-300 print:pb-2">
                      {(selectedOrder.order.status === 'completed' || selectedOrder.order.status === 'in_progress')
                        ? 'Ingredientes Consumidos' : 'Lista de Ingredientes'}
                    </h4>
                    {(selectedOrder.order.status === 'completed' || selectedOrder.order.status === 'in_progress') ? (
                      // Órdenes ya iniciadas/completadas: mostrar lo realmente consumido
                      <>
                        {selectedOrder.produced && (
                          <div className="mb-3 p-3 rounded-md border border-green-200 bg-green-50 text-sm flex flex-wrap gap-x-6 gap-y-1">
                            <span><span className="text-gray-500">Lote producido:</span> <span className="font-semibold text-gray-900">{selectedOrder.produced.internal_code}</span></span>
                            <span><span className="text-gray-500">Caducidad:</span> <span className="text-gray-900">{selectedOrder.produced.expiry_date ? fmtDate(selectedOrder.produced.expiry_date) : '-'}</span></span>
                          </div>
                        )}
                        <div className="border rounded-md overflow-hidden print:border-2 print:border-gray-400">
                          <table className="min-w-full divide-y divide-gray-200 print:divide-gray-400">
                            <thead className="bg-gray-50 print:bg-gray-100">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider print:text-sm print:py-2">Ingrediente</th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Marca</th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Lote real</th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Lote interno</th>
                                <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider print:text-sm print:py-2">Consumido</th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Caducidad</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200 print:divide-gray-300">
                              {(selectedOrder.consumptions || []).map((c: any, index: number) => (
                                <tr key={index}>
                                  <td className="px-4 py-3 text-sm font-medium text-gray-900 print:text-base print:py-4">{c.ingredient}</td>
                                  <td className="px-4 py-3 text-sm text-gray-700">{c.brand || <span className="text-gray-400">Sin marca</span>}</td>
                                  <td className="px-4 py-3 text-sm text-gray-700">{c.lot_number || '-'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-500">{c.internal_code || '-'}</td>
                                  <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900 print:text-base print:py-4">{parseFloat(String(c.quantity_g)).toFixed(2)} g</td>
                                  <td className="px-4 py-3 text-sm text-gray-700">{c.expiry_date ? fmtDate(c.expiry_date) : '-'}</td>
                                </tr>
                              ))}
                              {(!selectedOrder.consumptions || selectedOrder.consumptions.length === 0) && (
                                <tr><td colSpan={6} className="px-4 py-3 text-sm text-center text-gray-400">Sin consumos registrados</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                    <div className="border rounded-md overflow-hidden print:border-2 print:border-gray-400">
                      <table className="min-w-full divide-y divide-gray-200 print:divide-gray-400">
                        <thead className="bg-gray-50 print:bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider print:text-sm print:py-2">
                              Ingrediente
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider print:text-sm print:py-2">
                              Cantidad Necesaria
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider no-print">
                              Lote Asignado
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider no-print">
                              Disponible
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider no-print">
                              Faltante
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider no-print">
                              Estado
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider hidden print:table-cell print:text-sm print:py-2">
                              ✓
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200 print:divide-gray-300">
                          {selectedOrder.requirements.requirements.map((req: Requirement, index: number) => (
                            <tr key={index} className={req.availability.is_available ? '' : 'bg-red-50 no-print'}>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 print:text-base print:py-4">
                                {req.ingredient_name}
                              </td>
                              <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900 print:text-base print:py-4">
                                {req.required_quantity_g.toFixed(2)} g
                              </td>
                              <td className="px-4 py-3 text-sm text-left text-gray-700 no-print">
                                {lotsFor(req.ingredient_id)}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-900 no-print">
                                {req.availability.total_available_g.toFixed(2)} g
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-900 no-print">
                                {req.availability.shortage_g > 0 ? (
                                  <span className="text-red-600 font-semibold">
                                    {req.availability.shortage_g.toFixed(2)} g
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-center no-print">
                                {req.availability.is_available ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    ✓ OK
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    ✗ Insuficiente
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center hidden print:table-cell print:py-4">
                                <span className="inline-block w-4 h-4 border-2 border-gray-400"></span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    )}
                  </div>
                )}

                {/* Notes */}
                {selectedOrder.order.notes && (
                  <div className="print:break-inside-avoid">
                    <h4 className="text-md font-bold text-gray-900 mb-2 print:text-lg">Notas</h4>
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 print:bg-white print:border print:border-gray-300">
                      <p className="text-sm text-gray-700 print:text-base">{selectedOrder.order.notes}</p>
                    </div>
                  </div>
                )}

                {/* Signature Section (Print Only) */}
                <div className="hidden print:block print:mt-12 print:break-inside-avoid">
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-8">Elaborado por:</p>
                      <div className="border-t-2 border-gray-400 pt-2">
                        <p className="text-xs text-gray-600">Nombre y Firma</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-8">Verificado por:</p>
                      <div className="border-t-2 border-gray-400 pt-2">
                        <p className="text-xs text-gray-600">Nombre y Firma</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer (las acciones de impresión/faltantes están en la cabecera) */}
              <div className="flex justify-end gap-3 pt-4 border-t mt-6 no-print">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Complete Order Modal */}
      {showCompleteModal && orderToComplete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Completar Orden de Producción
              </h3>
              <button
                onClick={() => {
                  setShowCompleteModal(false)
                  setOrderToComplete(null)
                  setError('')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Order Info */}
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase">Código Orden</label>
                    <p className="mt-1 text-sm font-bold text-gray-900">{orderToComplete.code}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase">Receta</label>
                    <p className="mt-1 text-sm font-semibold text-gray-900">{orderToComplete.recipe.name}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase">Alias</label>
                    <p className="mt-1 text-sm text-gray-900">{orderToComplete.recipe.alias || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase">Cantidad Objetivo</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {parseFloat(orderToComplete.target_quantity).toFixed(2)} {orderToComplete.recipe.final_unit}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actual Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cantidad Real Producida ({orderToComplete.recipe.final_unit}) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={completeFormData.actual_quantity}
                  onChange={(e) => setCompleteFormData({ ...completeFormData, actual_quantity: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Se ha precargado la cantidad objetivo. Ajústala según lo realmente producido.
                </p>
              </div>

              {/* Allergens Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alérgenos
                </label>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3">
                  <p className="text-sm text-blue-700">
                    <strong>Nota:</strong> Los alérgenos marcados en azul son heredados de los ingredientes de la receta y no se pueden desmarcar.
                    Puedes agregar alérgenos adicionales si hubo contaminación cruzada durante el proceso de producción.
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
                  {allergens.map((allergen) => {
                    const isInherited = inheritedAllergens.includes(allergen.id)
                    const isExtra = completeFormData.extra_allergens.includes(allergen.id)
                    const isChecked = isInherited || isExtra

                    return (
                      <label
                        key={allergen.id}
                        className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          isInherited
                            ? 'bg-blue-50 border-blue-300 cursor-not-allowed'
                            : isExtra
                            ? 'bg-orange-50 border-orange-300 hover:bg-orange-100'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isInherited}
                          onChange={() => !isInherited && toggleExtraAllergen(allergen.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:cursor-not-allowed"
                        />
                        <span className="text-xl">{allergen.svg_icon?.startsWith('<svg') ? <span dangerouslySetInnerHTML={{ __html: allergen.svg_icon }} /> : allergen.svg_icon}</span>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {allergen.name}
                          </p>
                          {isInherited && (
                            <p className="text-xs text-blue-600">Heredado</p>
                          )}
                          {isExtra && !isInherited && (
                            <p className="text-xs text-orange-600">Contaminación</p>
                          )}
                        </div>
                      </label>
                    )
                  })}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas de Producción
                </label>
                <textarea
                  value={completeFormData.notes}
                  onChange={(e) => setCompleteFormData({ ...completeFormData, notes: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Observaciones sobre el proceso de producción..."
                />
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowCompleteModal(false)
                    setOrderToComplete(null)
                    setError('')
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleCompleteSubmit}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                >
                  Completar Orden
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Start Production Modal - Batch Selection */}
      {showStartModal && orderToStart && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-5xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Iniciar Producción - Selección de Lotes
              </h3>
              <button
                onClick={() => {
                  setShowStartModal(false)
                  setOrderToStart(null)
                  setBatchAllocations([])
                  setError('')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Order Info */}
              <div className="bg-blue-50 p-4 rounded-md">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-blue-600 uppercase">Orden</label>
                    <p className="mt-1 text-sm font-bold text-gray-900">{orderToStart.code}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-blue-600 uppercase">Receta</label>
                    <p className="mt-1 text-sm font-semibold text-gray-900">{orderToStart.recipe.name}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-blue-600 uppercase">Alias</label>
                    <p className="mt-1 text-sm text-gray-900">{orderToStart.recipe.alias || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-blue-600 uppercase">Cantidad</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {parseFloat(orderToStart.target_quantity).toFixed(2)} {orderToStart.recipe.final_unit}
                    </p>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Selecciona los lotes</strong> a utilizar para cada ingrediente. Los lotes sin abrir se marcarán como abiertos automáticamente.
                  Se recomienda usar primero los lotes con fecha de caducidad más próxima (FIFO).
                </p>
              </div>

              {/* Ingredients and Batch Selection */}
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {startRequirements.map((req) => {
                  const allocated = getTotalAllocated(req.ingredient_id)
                  const remaining = req.required_quantity_g - allocated
                  const isComplete = remaining <= 0.01

                  return (
                    <div key={req.ingredient_id} className={`border rounded-lg p-4 ${isComplete ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
                      <div className="flex justify-between items-center mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">{req.ingredient_name}</h4>
                          <p className="text-sm text-gray-500">
                            Necesario: <span className="font-semibold">{req.required_quantity_g.toFixed(2)} g</span>
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-semibold ${isComplete ? 'text-green-600' : 'text-orange-600'}`}>
                            Asignado: {allocated.toFixed(2)} g
                          </p>
                          {!isComplete && (
                            <p className="text-xs text-red-600">
                              Falta: {remaining.toFixed(2)} g
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Available Batches */}
                      <div className="space-y-2">
                        {req.availability.batch_allocation.length === 0 ? (
                          <p className="text-sm text-red-600 italic">No hay lotes disponibles para este ingrediente</p>
                        ) : (
                          req.availability.batch_allocation.map((batch) => {
                            const currentAlloc = batchAllocations
                              .find(a => a.ingredient_id === req.ingredient_id)
                              ?.batches.find(b => b.batch_id === batch.id)

                            return (
                              <div key={batch.id} className="flex items-center gap-3 bg-white p-2 rounded border">
                                <div className="flex-1">
                                  <p className="text-sm font-medium">
                                    Marca: {batch.brand} | Lote: {batch.lot_number} | Cad: {fmtDate(batch.expiry_date)}
                                    <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                                      batch.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {batch.status === 'open' ? 'Abierto' : 'Sin abrir'}
                                    </span>
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {batch.supplier_name} | Disponible: {parseFloat(String(batch.quantity_available_g)).toFixed(2)} g
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max={batch.quantity_available_g}
                                    value={currentAlloc?.quantity_g || ''}
                                    onChange={(e) => updateBatchAllocation(
                                      req.ingredient_id,
                                      batch.id,
                                      parseFloat(e.target.value) || 0
                                    )}
                                    placeholder="0"
                                    className="w-24 border border-gray-300 rounded px-2 py-1 text-sm text-right"
                                  />
                                  <span className="text-sm text-gray-500">g</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // Use maximum available or remaining needed, whichever is smaller
                                      const maxToUse = Math.min(parseFloat(String(batch.quantity_available_g)), remaining + (currentAlloc?.quantity_g || 0))
                                      updateBatchAllocation(req.ingredient_id, batch.id, maxToUse)
                                    }}
                                    className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                  >
                                    Max
                                  </button>
                                </div>
                              </div>
                            )
                          })
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowStartModal(false)
                    setOrderToStart(null)
                    setBatchAllocations([])
                    setError('')
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleStartSubmit}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Iniciar Producción
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: generar órdenes de compra de faltantes (proveedor por ítem) */}
      {showPurchaseModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white mb-10">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Generar órdenes de compra</h3>
              <button onClick={() => { setShowPurchaseModal(false); setPurchaseItems([]) }} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Asigna un proveedor a cada ingrediente. Se creará una orden de compra por cada proveedor.
            </p>
            <div className="border rounded-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-bold text-gray-700 uppercase">Ingrediente</th>
                    <th className="px-4 py-2 text-right text-xs font-bold text-gray-700 uppercase">Cantidad</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-gray-700 uppercase">Proveedor</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {purchaseItems.map((it, idx) => (
                    <tr key={it.ingredient_id}>
                      <td className="px-4 py-2 text-sm text-gray-900">{it.name}</td>
                      <td className="px-4 py-2 text-sm text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          <input
                            type="number"
                            min="0.001"
                            step="0.001"
                            value={it.quantity}
                            onChange={(e) => setPurchaseItems(prev => prev.map((p, i) => i === idx ? { ...p, quantity: parseFloat(e.target.value) || 0 } : p))}
                            className="w-24 border border-gray-300 rounded-md px-2 py-1 text-sm text-right"
                          />
                          <span className="text-gray-500">{it.unit_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <select
                          value={it.supplier_id}
                          onChange={(e) => setPurchaseItems(prev => prev.map((p, i) => i === idx ? { ...p, supplier_id: e.target.value } : p))}
                          className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                        >
                          <option value="">Seleccionar...</option>
                          {suppliers.map(s => (
                            <option key={s.id} value={s.id}>{s.trade_name}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => { setShowPurchaseModal(false); setPurchaseItems([]) }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                onClick={createPurchaseOrders}
                disabled={savingPurchase}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-transparent rounded-md hover:bg-amber-700 disabled:opacity-50"
              >
                {savingPurchase ? 'Creando…' : 'Crear órdenes de compra'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: generar órdenes de producción de faltantes elaborados */}
      {showProductionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white mb-10">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Producir elaborados faltantes</h3>
              <button onClick={() => { setShowProductionModal(false); setProductionItems([]) }} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Indica la cantidad objetivo de cada ingrediente elaborado. Se creará una orden de producción por cada uno (en estado borrador).
            </p>
            <div className="border rounded-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-bold text-gray-700 uppercase">Ingrediente elaborado</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-gray-700 uppercase">Cantidad objetivo</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {productionItems.map((it, idx) => (
                    <tr key={it.ingredient_id}>
                      <td className="px-4 py-2 text-sm text-gray-900">{it.name}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="0.001"
                            step="0.001"
                            value={it.target_quantity}
                            onChange={(e) => setProductionItems(prev => prev.map((p, i) => i === idx ? { ...p, target_quantity: parseFloat(e.target.value) || 0 } : p))}
                            className="w-28 border border-gray-300 rounded-md px-2 py-1 text-sm text-right"
                          />
                          <span className="text-gray-500 text-sm">{it.unit_name}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => { setShowProductionModal(false); setProductionItems([]) }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                onClick={createProductionOrders}
                disabled={savingProduction}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {savingProduction ? 'Creando…' : 'Crear órdenes de producción'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
